import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ClockIcon, XIcon } from "@heroicons/react/outline";
import * as api from "./apiService";

const STATUS_LABELS = {
  U: "Submitted",
  A: "Received",
  S: "Assigned",
  W: "Work In Progress",
  R: "Review",
  C: "Completed",
  T: "Canceled",
  I: "Invoiced",
  N: "Not Invoiced",
  X: "Unassigned",
};

const FIELD_LABELS = {
  E: "Departure changed",
  A: "Arrival changed",
  B: "Complete Before changed",
  O: "Airport changed",
  F: "FBO changed",
  T: "Tail Number changed",
  U: "Photos uploaded",
  R: "Job returned",
  V: "Accepted",
};

function formatTimestamp(value, includeTime = true) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(includeTime ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(date);
}

function activityTitle(activity) {
  if (activity.activity_type === "S") {
    if (activity.status === "P") return `Price changed to $${activity.price}`;
    return `Status changed to ${STATUS_LABELS[activity.status] || activity.status}`;
  }
  if (activity.activity_type === "P") return `Price changed to $${activity.price}`;
  if (activity.activity_type === "C") return `Service added: ${activity.service_name || "Service"}`;
  if (activity.activity_type === "D") return `Service removed: ${activity.service_name || "Service"}`;
  if (activity.activity_type === "X") return `Retainer added: ${activity.service_name || "Service"}`;
  if (activity.activity_type === "Y") return `Retainer removed: ${activity.service_name || "Service"}`;
  return FIELD_LABELS[activity.activity_type] || "Job updated";
}

function activityActor(activity) {
  const nestedName = [activity.user?.first_name, activity.user?.last_name]
    .filter(Boolean)
    .join(" ");
  const name = activity.user_full_name || nestedName;
  return !name || name === "None" ? "Livetakeoff" : name;
}

function serviceChange(row) {
  const action = row.change || row.action || (row.status === "C" ? "completed" : row.status === "W" ? "started" : "updated");
  return `${row.service_name || "Service"} ${action}`;
}

export default function JobActivityDrawer({ open, onClose, jobId, currentUser }) {
  const [activities, setActivities] = useState([]);
  const [serviceActivities, setServiceActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      api.getJobActivities(jobId),
      api.getJobServiceActivities({ job_id: jobId }),
    ])
      .then(([jobResponse, serviceResponse]) => {
        if (!active) return;
        setActivities(jobResponse.data?.results || []);
        setServiceActivities(serviceResponse.data?.results || []);
      })
      .catch(() => {
        if (active) setError("Unable to load job activity.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [jobId, open]);

  const visibleActivities = useMemo(() => {
    if (!currentUser?.isCustomer) return activities;
    return activities.filter((activity) => !["C", "D", "X", "Y"].includes(activity.activity_type));
  }, [activities, currentUser]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[11000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-200"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto flex h-full w-[92vw] max-w-[384px] flex-col border-l border-slate-200 bg-white text-sm shadow-xl">
                  <div className="relative border-b border-slate-200 px-5 py-5">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-[#E3222A]" aria-hidden="true" />
                      <Dialog.Title className="text-lg font-extrabold text-slate-950">Activity</Dialog.Title>
                    </div>
                    <Dialog.Description className="mt-1 text-sm text-slate-500">
                      Job events and service-level history remain distinct.
                    </Dialog.Description>
                    <button
                      type="button"
                      onClick={onClose}
                      className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                    >
                      <XIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Close</span>
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-7">
                    {loading && (
                      <div className="space-y-4" aria-label="Loading activity">
                        {[1, 2, 3].map((item) => <div key={item} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}
                      </div>
                    )}

                    {!loading && error && (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
                    )}

                    {!loading && !error && (
                      <>
                        <section>
                          <h3 className="text-sm font-extrabold text-slate-950">Job activity</h3>
                          {visibleActivities.length === 0 ? (
                            <p className="mt-3 text-sm text-slate-500">No job activity yet.</p>
                          ) : (
                            <ol className="mt-4">
                              {visibleActivities.map((activity, index) => (
                                <li key={activity.id ?? index} className="relative flex gap-3 pb-5 last:pb-0">
                                  {index < visibleActivities.length - 1 && (
                                    <span className="absolute left-[5px] top-3 h-full w-px bg-slate-300" aria-hidden="true" />
                                  )}
                                  <span className="relative mt-1.5 h-3 w-3 flex-none rounded-full border-2 border-white bg-[#E3222A] ring-1 ring-slate-300" aria-hidden="true" />
                                  <div className="min-w-0">
                                    <p className="font-bold leading-5 text-slate-900">{activityTitle(activity)}</p>
                                    <p className="mt-0.5 text-xs leading-5 text-slate-500">
                                      {activityActor(activity)}
                                      {!currentUser?.isCustomer && activity.timestamp && ` · ${formatTimestamp(activity.timestamp)}`}
                                    </p>
                                  </div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </section>

                        <section className="mt-7">
                          <h3 className="text-sm font-extrabold text-slate-950">Service activity</h3>
                          <p className="mt-1 text-xs text-slate-500">History of service changes for this job.</p>
                          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
                            <div className="grid grid-cols-[92px_minmax(0,1fr)] bg-slate-50 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
                              <span>Date</span>
                              <span>Service change</span>
                            </div>
                            {serviceActivities.length === 0 ? (
                              <p className="border-t border-slate-200 px-4 py-5 text-sm text-slate-500">No service activity yet.</p>
                            ) : (
                              <div className="divide-y divide-slate-100 border-t border-slate-200">
                                {serviceActivities.map((row, index) => (
                                  <div key={row.id ?? index} className="grid grid-cols-[92px_minmax(0,1fr)] gap-3 px-4 py-3">
                                    <span className="text-sm text-slate-500">{formatTimestamp(row.timestamp, false)}</span>
                                    <span className="text-sm font-semibold leading-5 text-slate-900">{serviceChange(row)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </section>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
